import { autoinject, bindable, bindingMode, observable } from "aurelia-framework";
import { appLogger } from "../../infrastructure/logging";
import { ChallengeService } from "./../../services/challenge-service";
import {
  ValidationController,
  ValidationControllerFactory,
  validateTrigger,
  ValidationRules
} from "aurelia-validation";
import { LoginStage } from "../../enums/login-stage";
import { BootstrapFormRenderer } from "../../components/bootstrap-form-renderer";
import { localGlobal } from "../../infrastructure/infrastructure";
import { IdentityClientHttpError, IRedirectErrorContainer } from "../../infrastructure/identity-client";

@autoinject
export class PasswordForm {
  // fromView -subsequent updates made to stage in the parent doesn't flow into the child. Hence, we have bindingMode for stage as fromView
  @bindable({ defaultBindingMode: bindingMode.fromView })
  public stage: LoginStage;
  @bindable({ defaultBindingMode: bindingMode.toView })
  public email: string;
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public submitSecondFactor: () => Promise<boolean>;
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public finalize: () => Promise<void>;

  private _challengeService: ChallengeService;
  public hasCredError: boolean = false;
  public isLoading: boolean = false;
  public passwordFocused: boolean = true;
  @observable
  public password: string;
  public passwordController: ValidationController;
  public rememberMe:boolean=false;
  public showPassword: boolean = false;
  public showPasswordEye: boolean = false;

  public constructor(
    challengeService: ChallengeService,
    controllerFactory: ValidationControllerFactory
  ) {
    this._challengeService = challengeService;
    this.passwordController = controllerFactory.create();
    this.passwordController.validateTrigger = validateTrigger.changeOrBlur;
    this.passwordController.addObject(this, passwordRules);
    this.passwordController.addRenderer(new BootstrapFormRenderer());
  }

  public attached(){
    const getEmail = JSON.parse(localStorage.getItem("emailSave"));
    if ( getEmail ) {
      this.rememberMe=true;
    }
  }

  public executeRecaptcha() {
    localGlobal.grecaptcha.enterprise.ready(() => {
      localGlobal.grecaptcha.enterprise.execute(localGlobal._recaptchaSiteKey, { action: 'LOGIN' })
      .then((token: string) => {
        this.login(token);
      });
    });
  }

  public async login(gRecaptchaResponse: string) {
    if(this.rememberMe===true){
      localStorage.setItem("emailSave", JSON.stringify(this.email));
    }
    if(this.rememberMe===false && JSON.parse(localStorage.getItem("emailSave"))){
      localStorage.removeItem("emailSave");
    }
    let res = await this.passwordController.validate();
    if (!res.valid) {
      return;
    }
    let credentialsSuccess = await this.submitCredentials(gRecaptchaResponse);
    if (!credentialsSuccess) {
      return;
    }
    let secondFactorSuccess = await this.submitSecondFactor();
    if (!secondFactorSuccess) {
      return;
    }
    await this.finalize();
  }

  public async submitCredentials(gRecaptchaResponse: string) {
    this.isLoading = true;
    try{
      let result = await this._challengeService.challengeEndUserCredential(
        this.email,
        this.password,
        gRecaptchaResponse
      );
      let failure = result == null;
      this.hasCredError = failure;
      if (failure) {
        this.stage = LoginStage.password;
        this.passwordFocused = true;
        this.password = null;
        appLogger.warn("Credential check failed.");
      }
      return !failure;
    } catch(error) {
      if(error as IdentityClientHttpError) {
        var identityError = (error as IdentityClientHttpError);
        var errorResponse = identityError.response as unknown as IRedirectErrorContainer;
        if(identityError.status === 403) {
          window.location.assign(errorResponse.redirectUrl);
          return false;
        }
        if(errorResponse.error?.includes('Invalid login information')){
          this.hasCredError = true;
        }
      }
      this.stage = LoginStage.password;
      this.passwordFocused = true;
      this.password = null;
      appLogger.warn("Credential check failed.");
      return false;
    }finally { 
      this.isLoading = false;
    }
  }

  public clearPassword() {
    this.password = null;
  }

  public toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  public passwordChanged(newValue: any) {
    if(newValue) {
        this.showPasswordEye = true;
    } else {
        this.showPasswordEye = false;
    }    
  }

  // public async verifyDevice() {
  //   let blindCheck = await this.load(() =>
  //     this._challengeService.checkDeviceChallenge(null)
  //   );
  //   if (blindCheck == null) {
  //     this.stage = LoginStage.device;
  //     let challenge = await this.load(() =>
  //       this._challengeService.requestDeviceChallenge()
  //     );
  //     this.challengeId = challenge.challengeId;
  //     this.challengeExpires = challenge.expires;
  //     appLogger.info(
  //       `Challenge ${this.challengeId} created, it expires ${this.challengeExpires}.`
  //     );

  //     let success = false;
  //     do {
  //       await sleep(2000);
  //       let checkResult = await this.load(() =>
  //         this._challengeService.checkDeviceChallenge(this.challengeId)
  //       );
  //       success = checkResult != null;
  //     } while (!success);
  //   }
  //   return true;
  // }
}

const passwordRules = ValidationRules.ensure((x: PasswordForm) => x.password)
  .required()
  .minLength(8)
  .maxLength(256).rules;
